import { useSyncExternalStore } from 'react';
import * as z from 'zod';
import { FAVORITE_KEY } from '@/constants';
import { readJsonFromStorage } from '@shared/storage';

/**
 * A favorite is a browser-style bookmark: a pointer back to an article
 * inside the online issue reader, plus just enough display text to list it
 * without fetching anything. It deliberately does *not* hold the article's
 * content or styles -- opening one navigates to `path` and the reader
 * loads it like any other visit (TanStack Query's cache, not Dexie). Dexie
 * stays reserved for explicit offline downloads (`entities/downloaded-
 * issue`).
 *
 * `path` doubles as the identity: the same article reached twice is the
 * same favorite.
 */
export const FavoriteSchema = z.object({
	path: z.string(),
	title: z.string(),
	description: z.string().optional(),
	issueTitle: z.string(),
	createdAt: z.number(),
});

export type Favorite = z.infer<typeof FavoriteSchema>;

const FavoritesSchema = z.array(FavoriteSchema);

// Pre-bookmark builds stored a `number[]` of article ids under this same
// key. That shape fails `FavoritesSchema` and reads as an empty list --
// those favorites pointed at Dexie copies that no longer have a UI, so
// there's nothing meaningful to migrate them into.
const EMPTY: Array<Favorite> = [];

const listeners = new Set<() => void>();

// `useSyncExternalStore` requires a referentially stable snapshot between
// changes, so the parsed list is cached against the raw string it came
// from. Keying on the raw value (rather than invalidating only on our own
// writes) also picks up writes made behind this module's back --
// `clearOfflineContent`'s `removeItem` on login/logout, another tab.
let cachedRaw: string | null | undefined;
let cachedList: Array<Favorite> = EMPTY;

function readFavorites(): Array<Favorite> {
	const raw = localStorage.getItem(FAVORITE_KEY);
	if (raw !== cachedRaw) {
		cachedRaw = raw;
		cachedList = readJsonFromStorage(FAVORITE_KEY, FavoritesSchema, EMPTY);
	}
	return cachedList;
}

function writeFavorites(favorites: Array<Favorite>): void {
	localStorage.setItem(FAVORITE_KEY, JSON.stringify(favorites));
	for (const listener of listeners) {
		listener();
	}
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	// `storage` only fires for *other* tabs' writes -- same-tab writes go
	// through `writeFavorites`'s own notify above.
	const onStorage = (event: StorageEvent) => {
		if (event.key === FAVORITE_KEY || event.key === null) {
			listener();
		}
	};
	window.addEventListener('storage', onStorage);
	return () => {
		listeners.delete(listener);
		window.removeEventListener('storage', onStorage);
	};
}

export function addFavorite(favorite: Omit<Favorite, 'createdAt'>): void {
	const rest = readFavorites().filter((f) => f.path !== favorite.path);
	// Newest first -- the order the drawer lists them in.
	writeFavorites([{ ...favorite, createdAt: Date.now() }, ...rest]);
}

export function removeFavorite(path: string): void {
	writeFavorites(readFavorites().filter((f) => f.path !== path));
}

export function useFavorites(): Array<Favorite> {
	return useSyncExternalStore(subscribe, readFavorites, () => EMPTY);
}

export function useIsFavorite(path: string | null): boolean {
	const favorites = useFavorites();
	return !!path && favorites.some((f) => f.path === path);
}
